// pendingIntent.ts — carries "what the visitor was trying to do" across signup.
//
// The problem it solves: a logged-out buyer on /pricing is sent to
// /?auth=signup&intent=pro:annual, Redeem sends intent=redeem:<code>, and an
// alert email's /app?property=<id> opens the signup wall. Signup needs an email
// confirmation, and the confirmation link opens a NEW tab at /auth/callback,
// which forwards to /app. That tab has a fresh sessionStorage and a URL with
// none of the original query params, so before this module the intent was
// simply gone: the buyer landed on the free map with no way back to checkout.
//
// localStorage (shared by every tab of the same origin) carries it instead,
// with a timestamp so a stale intent from a signup abandoned days ago can't
// ambush the user later. Every read is wrapped: private browsing and blocked
// storage throw, and the app must behave exactly as before when that happens.
//
// Limit: a confirmation link opened in a DIFFERENT browser or device has no
// access to this storage, so the intent is lost there. That is the same
// boundary every client-side approach has; carrying the intent through the
// Supabase emailRedirectTo URL would need that URL added to the auth
// redirect allowlist, which is a dashboard change, not a code change.

export const PENDING_INTENT_KEY = "parcely.pendingIntent";
// Long enough to cover "signed up, confirmed after lunch", short enough that a
// buyer who walked away is not ambushed by a checkout prompt next week.
export const PENDING_INTENT_TTL_MS = 24 * 60 * 60 * 1000;

export type PaidPlanIntent = "scout" | "pro";
export type BillingInterval = "monthly" | "annual";

export type PendingIntent =
  | { kind: "plan"; plan: PaidPlanIntent; interval: BillingInterval }
  | { kind: "redeem"; code: string }
  | { kind: "property"; propertyId: string };

export interface StoredIntent {
  intent?: PendingIntent;
  /** A county the visitor arrived for (county landing page CTA). */
  countyId?: string;
  savedAt: number;
}

// Conservative token shapes. Everything here round-trips into a URL or an API
// call, so anything unexpected is dropped rather than passed along.
const CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;
const ID_RE = /^[A-Za-z0-9-]{1,64}$/;
const COUNTY_RE = /^[a-z0-9-]{1,64}$/;

export function isValidCountySlug(raw: string | null | undefined): raw is string {
  return !!raw && COUNTY_RE.test(raw);
}

export function isValidPropertyId(raw: string | null | undefined): raw is string {
  return !!raw && ID_RE.test(raw);
}

/**
 * Parse the `intent` query param Pricing.tsx ("pro:annual") and Redeem.tsx
 * ("redeem:CODE") put on the signup URL. Returns null for anything it does
 * not recognize. Team is deliberately not a plan intent: it is sold through
 * the "I'm interested" form, never through checkout.
 */
export function parseIntentParam(raw: string | null | undefined): PendingIntent | null {
  if (!raw) return null;
  const sep = raw.indexOf(":");
  const head = (sep === -1 ? raw : raw.slice(0, sep)).trim().toLowerCase();
  const tail = sep === -1 ? "" : raw.slice(sep + 1).trim();
  if (head === "redeem") {
    // An empty code means the visitor opened /redeem without one: after
    // signing in they go back to the blank redeem form.
    if (tail === "") return { kind: "redeem", code: "" };
    return CODE_RE.test(tail) ? { kind: "redeem", code: tail } : null;
  }
  if (head === "scout" || head === "pro") {
    const interval = tail.toLowerCase();
    if (interval === "" || interval === "monthly") return { kind: "plan", plan: head, interval: "monthly" };
    if (interval === "annual") return { kind: "plan", plan: head, interval: "annual" };
    return null;
  }
  return null;
}

function isIntent(v: unknown): v is PendingIntent {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  if (o.kind === "plan") {
    return (o.plan === "scout" || o.plan === "pro") && (o.interval === "monthly" || o.interval === "annual");
  }
  if (o.kind === "redeem") return typeof o.code === "string" && (o.code === "" || CODE_RE.test(o.code));
  if (o.kind === "property") return typeof o.propertyId === "string" && ID_RE.test(o.propertyId);
  return false;
}

/** The stored intent, or null when absent, corrupt or older than the TTL
 *  (an expired or corrupt entry is removed on the way out). */
export function readPendingIntent(now: number = Date.now()): StoredIntent | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_INTENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const savedAt = typeof parsed.savedAt === "number" ? parsed.savedAt : NaN;
    if (!Number.isFinite(savedAt) || now - savedAt > PENDING_INTENT_TTL_MS || savedAt - now > 60_000) {
      clearPendingIntent();
      return null;
    }
    const out: StoredIntent = { savedAt };
    if (isIntent(parsed.intent)) out.intent = parsed.intent;
    if (typeof parsed.countyId === "string" && isValidCountySlug(parsed.countyId)) out.countyId = parsed.countyId;
    if (!out.intent && !out.countyId) {
      clearPendingIntent();
      return null;
    }
    return out;
  } catch {
    clearPendingIntent();
    return null;
  }
}

/** Merge an intent and/or county into whatever is already stored. A new
 *  intent replaces an old one (the latest click is what the visitor wants);
 *  the timestamp is refreshed either way. */
export function savePendingIntent(
  update: { intent?: PendingIntent | null; countyId?: string | null },
  now: number = Date.now()
): void {
  const prev = readPendingIntent(now);
  const next: StoredIntent = { savedAt: now };
  const intent = update.intent === undefined ? prev?.intent : update.intent ?? undefined;
  const countyId = update.countyId === undefined ? prev?.countyId : update.countyId ?? undefined;
  if (intent) next.intent = intent;
  if (countyId && isValidCountySlug(countyId)) next.countyId = countyId;
  if (!next.intent && !next.countyId) {
    clearPendingIntent();
    return;
  }
  try {
    localStorage.setItem(PENDING_INTENT_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked — the intent just does not survive confirmation */
  }
}

export function clearPendingIntent(): void {
  try {
    localStorage.removeItem(PENDING_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

const PLAN_RANK: Record<string, number> = { free: 0, scout: 1, pro: 2, team: 3, admin: 4 };

/** Whether a plan intent is still worth offering: only when it is an upgrade
 *  over what the user already has. A Pro user who clicked "Scout" before
 *  signing in to an existing account is not sent to buy a downgrade. */
export function planIntentIsUpgrade(currentPlan: string, intentPlan: PaidPlanIntent): boolean {
  return (PLAN_RANK[currentPlan] ?? 0) < (PLAN_RANK[intentPlan] ?? 0);
}

/** The /pricing URL for a plan intent. Pricing.tsx does not read these params
 *  yet; they are the contract for it to preselect the plan + interval. */
export function pricingUrlForIntent(intent: { plan: PaidPlanIntent; interval: BillingInterval }): string {
  return `/pricing?plan=${intent.plan}&interval=${intent.interval}`;
}
